/* eslint-disable no-unused-vars */
const THREE = window.THREE;

import Vue from 'vue';
import Vuex from 'vuex';
import config from '../../appConfig';
import { getBoxOffset } from '../utils/threejsUtils';
import { checkIsModelTrasnlated } from '../utils/forgeUtils';
import { scaleFromDisplayUnit } from '../utils/coordinateAuthority';
import { ForgeViewerService } from './../scripts/js/forgeService';
import { ForgeProject, ForgeSettings, UserSettings } from './../scripts/js/forgeModels';
import {
  dispose2DViewer,
  getViewPage2d,
  launchPlanViewer,
  getMessagePopup,
  disposeForgeViewers,
} from './../scripts/js/forgeviewer';
import HttpService from '@/utils/httpService';
import { FACILITY_REQUEST } from '@/utils/http/RequestRegistry';
import { getStreamingApi } from '@/utils/regionUtils';
import { getModelOffsets } from '@/services/forge.service';
import { MEASUREMENT } from './modules/measurement';
import { TAGS } from './modules/tags';
import { MARKUPS } from './modules/markups';
import { ModelBrowser } from './modules/modelBrowser';
import { Attachments } from './modules/attachments';
import { Viewers } from './modules/viewers';
import { timeline } from './modules/timeline';
import { InspectionDashboard } from './modules/inspection-dashboard';
import { SharedDashboardSettings } from './modules/shared-dashboard-settings';
import { SisTestingDashboard } from './modules/sis-testing-dashboard';
import { WorkPackProgressDashboard } from './modules/work-pack-progress-dashboard';
import { ACCOUNT } from '@Account/modules/account.modules.js';
import { FEEDBACK } from '@Feedback/modules/feedback.modules.js';
import { FEEDBACK_MANAGEMENT } from '@Feedback/modules/feedbackManagement.modules.js';
import { GENERAL } from '@Share/modules/general.modules.js';
import { FACILITY } from '@Facility/modules/facility.modules.js';
import { COMPANY } from '@Company/modules/company.modules.js';
import { RELEASE_NOTES } from '@ReleaseNotes/modules/releaseNotes.modules.js';
import { ADMIN } from '@Admin/modules/admin.modules.js';
import { USER } from '@User/modules/user.modules.js';
import { ROLE } from '@Role/modules/role.modules.js';
import { TASK_MANAGER } from '@TaskManager/modules/taskManager.modules.js';
import { PERMISSION } from '@Role/modules/permission.modules.js';
import { USER_INVITATION } from '@User/modules/userInvitation.modules.js';
import { CONTRACT } from '@Contract/modules/contract.modules.js';
import { CONTRACTOR } from '@Contractors/modules/contractor.modules.js';
import { PROJECT } from '@Contract/modules/project.modules.js';
import { TASK } from '@Task/modules/task.modules.js';
import { LINK } from '@Task/modules/links.modules.js';
import { TASKSTATUS } from '@Task/modules/task-status.modules.js';
import { $getURLParameterVal } from '@Utils';
import { PANEL_TAB } from '@/constants/tabs';
import { PANEL_IDS } from '@/constants/panelIds';

/**
 * Mapping from legacy PANEL_TAB indices to new panel IDs
 * Bridges the old tab system with the new GENERAL/OPEN_PANEL system
 */
const TAB_TO_PANEL_MAP = {
  [PANEL_TAB.SEARCH]: PANEL_IDS.GLOBAL_SEARCH,
  [PANEL_TAB.BOOKMARK]: PANEL_IDS.BOOKMARKS,
  [PANEL_TAB.PLANS_2D]: PANEL_IDS.PLANS_2D,
  [PANEL_TAB.SCANS]: PANEL_IDS.SCANS,
  [PANEL_TAB.TAGS]: PANEL_IDS.TAGS,
  [PANEL_TAB.DOCUMENTS]: PANEL_IDS.DOCUMENTS,
  [PANEL_TAB.SCAN_DOWNLOAD]: PANEL_IDS.SCAN_DOWNLOAD,
  [PANEL_TAB.INSPECTION]: PANEL_IDS.CONDITION_MAP,
  [PANEL_TAB.MODELS]: PANEL_IDS.MODELS,
};

import { TASK_COMMENTS } from '@Task/modules/task-comments.modules';
import { TASK_HISTORY } from '@Task/modules/task-history.modules';
import { GANTT } from '@/features/Task/modules/gantt.modules';
import { SCHEDULER } from '@/features/Task/modules/scheduler.modules.js';
import { VERIFICATION } from '@/features/Verification/modules/verification.module.js';
import { AVA_SNAPS } from '@/features/Tags/snaps/avaSnaps.module.js';
import { AVA_FINDINGS } from '@/features/Tags/snaps/avaFindings.module.js';
import { TAGS_TYPES } from '@Tags/modules/tagsTypes.modules';
import { TAGS_REGISTER } from '@Tags/modules/tagsRegister.modules';
import { EQUIPMENT_VIEW } from '@/features/Tags/modules/equipmentView.modules';
import { PIPELINE_VIEW } from '@/features/Tags/modules/pipelineView.modules';
import { STRUCTURAL_VIEW } from '@/features/Tags/modules/structuralView.modules';
import { OPERATION_VIEW } from '@/features/Tags/modules/operationView.modules';
import router from '@/router';
import { INSPECTION_VIEW } from '@/features/Tags/modules/inspectionView.modules';
import { AVA_INSPECT } from '@/features/Tags/modules/avaInspect.modules';
import { AI_INSPECT } from '@/features/Dashboard/modules/aIInspect.modules';
import { AI_ANALYSIS } from '@/features/Dashboard/modules/aiAnalysis.modules';
import CORROSION_ANALYSER from '@/features/Dashboard/modules/corrosionAnalyser.module';
import { TASK_DOCUMENT } from '@Task/modules/task-documents.modules';
import { DOCUMENT_TYPE } from '@Task/modules/document-type.modules';
import { NOTIFICATIONS } from '@Share/modules/notifications.modules';
import { DASHBOARD } from '@/features/Dashboard/modules/dashboard.modules';
import { VIEWERS_MODE } from '@/features/Dashboard/modules/viewersMode.modules';
import { fetchAssetAnnotations, fetchAssetBookmarks, fetchAssetCategories, fetchAssetInspections, fetchAssetManualPipelines, fetchAssetManualStructural, fetchAssetModel, fetchAssetModels, fetchAssetNormalEquipment, fetchAssetPointCloudDownloads, fetchAssetPointCloudModels, fetchAssetScaffold, fetchUserSettings } from '@/utils/mongoUtils';
import projectsService from '@/features/Admin/services/projects.service';
import levelsService from '@/features/Admin/services/levels.service';
import { handleAssetInfo } from './helpers';
import { RBI_DASHBOARD } from '@/features/Dashboard/modules/rbi-dashboard.modules';
import { DASHBOARD_FILTERS } from '@/features/Dashboard/modules/dashboard-filters.modules';
import { GLOBAL_DASHBOARD_FILTERS } from '@/features/Dashboard/modules/global-dashboard-filters.modules';
import { AREAS } from '@/features/Admin/modules/areas.modules';
import { REGISTERS } from '@/features/Registers/store';
import { MASTERS } from '@/features/Masters/store';
import { TASK_IMPORT } from '@/features/TaskImport/store';
import { DOCUMENT_IMPORTS } from '@/features/DocumentImports/store';
import { MODELS } from '@/components/views/smallModels/modules/models.modules';
import { INSPECTION_DASHBOARD } from '@/features/Dashboard/modules/inspection-dashboard.modules';
import { THICKNESS_DASHBOARD } from '@/features/Dashboard/modules/thickness-dashboard.modules';
import { CATHODIC_DASHBOARD } from '@/features/Dashboard/modules/cathodic-dashbaord.modules';
import { IOW_DASHBOARD } from '@/features/Dashboard/modules/iow-dashboard.modules';
import { GlobalSearch } from '@/components/search/modules/globalSearch.modules';
import { EMPTY_SCAN_INDEX } from '@/components/views/images360/scanIndex';
import scanIndexPlugin from './plugins/scanIndexPlugin';
import { TAGS_GROUPS } from '@/features/Tags/modules/tagsGroups.modules';
import { GROUP_VIEW } from '@/features/Tags/modules/groupView';
import { ANOMALY_VIEW } from '@/features/Tags/modules/anomalyView.modules';
import { ADMIN_MODELS } from '@/features/Admin/modules/adminModels.modules';
import { MODEL_VERSIONS } from '@/store/modules/modelVersions.modules';
import { CLIENT } from '@/features/Client/modules/client.modules';
import { GROUPS } from '@Groups/modules/groups.modules';
import { BLOB_STORAGE } from '@/features/Admin/modules/blobstorage.modules';
import { DOWNLOAD_MANAGER } from './modules/downloadManager';
import { DOCUMENT_UPLOADS } from './modules/documentUploads';
import { TOAST } from './modules/toast';
import { CML_POPUP } from './modules/cmlPopup';
import { NAVIGATION_HISTORY } from './modules/navigation-history.modules';
import { SHARE_INTENT } from './modules/shareIntent';
import { SCANS_PANEL } from './modules/scansPanel';
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';
import permissions from '@Share/config/permissions';
import { $has } from '@/utils';
import {
  TAG_MARKER_OUTCOME,
  invalidateTagAnchorInMarkerArrays,
  removeTagIdFromMarkerArrays,
  upsertTagById,
} from '@/features/Tags/utils/tagMarkerLifecycle';

Vue.use(Vuex);

// Facility bootstrap and a cold spatial deep link can request the same image
// catalog concurrently. Keep one in-flight request per facility so navigation
// can await the bootstrap request instead of racing it or issuing a duplicate.
const facilityProjectsRequests = new Map();

const store = new Vuex.Store({
  state: {
    forgeService: new ForgeViewerService(),
    // PanoramaCompass overlay state. Lifted here (round 5) because class-instance
    // properties on forgeService are NOT reactive in Vue 2 — pageView.vue's
    // computed could not track fs.panoramaCompassState through the class
    // boundary. Declaring as top-level Vuex state guarantees reactivity.
    panoramaCompassState: {
      isReady: false,
      northOffset: null,
      isInteracting: false,
    },
    // AVA-437 #7a: reactive flag mirroring panoramaCompassState.isInteracting,
    // but for the Forge (3D / document-sheet) viewer. Flipped true while the
    // user is actively panning/zooming the Forge viewer (driven by the
    // CAMERA_CHANGE_EVENT handler + a 300ms coast window in forgeService). The
    // popup positioning rAF loop (usePopupCard._tickPositioning) reads this to
    // pause its layout-forcing getBoundingClientRect during the gesture —
    // Forge dirties layout each frame, so the rect read forces a synchronous
    // reflow that makes pan/zoom feel laggy while a CML/TML popup is open.
    forgeViewerInteracting: false,
    markupsComponent: null,
    markupsApp: null,
    currentUrn: null,
    currentApp: null,
    pdfViewerLoaded: false,
    viewerMouseDownEvent: null,
    mouseDownHitTest: null,
    adminAppContainer: null,
    markupImageHeight: 8,
    markupImageWidth: 8,
    isComparingMode: false,

    debugMode: config.debugMode,
    assetId: $getURLParameterVal('assetId'),
    assetName: $getURLParameterVal('assetName', true),
    contractName: $getURLParameterVal('contractName', true),
    clientName: $getURLParameterVal('clientName', true),
    userId: $getURLParameterVal('userId'),
    defaultViewType: $getURLParameterVal('view'),
    username: $getURLParameterVal('username', true),
    // email: $getURLParameterVal('email', true),
    parentSiteUrl: $getURLParameterVal('url', true),
    parentSiteName: $getURLParameterVal('type'),
    pointCloudContainer: $getURLParameterVal('pcAzureContainerURL', true),
    isMarkupImage: $getURLParameterVal('isMarkupImage') == 1,
    isAdminUser: $getURLParameterVal('isAdmin') == 1 ? true : false,
    parentModuleUrl: $getURLParameterVal('moduleUrl') ? $getURLParameterVal('moduleUrl', true) : '',
    testPackageId: $getURLParameterVal('referenceContextId'),
    testPackageNumber: $getURLParameterVal('testPackageNumber', false),
    parentAPI: $getURLParameterVal('parentApi', true) ?? '',
    parentBlob: $getURLParameterVal('parentBlob', true),
    currentInputElement: null,
    virtualKeyboardShown: false,
    dataFetched: false,
    isLargeModel: false,
    shareFileToken: null,
    isGeometryLoaded: false,
    isTreeLoaded: false,
    cropBoxApplied: false,
    pinnedPoint2d: null,
    pinnedPoint3d: null,
    pinnedPoint360: null,
    referencePoint360: null,
    scanSelectorReferencePoint: null,
    showScanSelectorPanelLoading: false,
    hasVisibleIndividualScans: false,
    cropFromNearbySelector: false,
    nearbyCropRadius: 2.5, // DEFAULT_NEARBY_CROP_RADIUS — must match ScanSelectorPanel default appliedRange
    savedViewConfig: null,
    isPickingTag: false,
    isPickingSubTag: false,
    isPickingCropOrigin: false,
    isDrawingAreaToDownloadPC: false,
    isPickingPin: false,
    isPickingCmlPoint: false,
    pickingCmlCallback: null,
    cmlAutoLoadedPcName: null,
    // Tag position assignment (follows CML picking pattern)
    isSettingTagPosition: false,
    positionAssignmentTag: null,
    positionAssignmentCallback: null,
    positionAutoLoadedPcName: null,
    forgeSettings: new ForgeSettings(),
    userSettings: new UserSettings(),
    categories: [],
    showAddAutoTagsPopup: false,
    showCreateInspectionPopup: false,
    tagsToCreateInspection: [],
    selectedDbids: [],
    tags: [],
    addRemove3dModeEnabled: false,
    isEmptyModel: false,
    model3dAvailable: false,
    globalOffset: new THREE.Vector3(0, 0, 0),
    unitScale: 0,
    model3dId: null,

    isDisplaySettingsVisible: false,
    isModelBrowserVisible: false,

    //ghosting
    isGhostingOn: false,
    isGhostingWireframe: true,
    ghostingOpacity: 100,
    ghostingColor: '#cad3db',
    isGreyModel: false,
    // A spatial-focus swap is waiting on the server while the previous scene stays on screen;
    // the 3D page shows its progress bar so the click visibly registered (useSpatialFocus).
    spatialFocusSwapPending: false,

    selectedTab: -1,
    isTabShown: false,

    //global search
    filteredTags: [],
    filteredEquipment: [],
    filteredPipelines: [],
    filteredStructural: [],
    filteredAnomalies: [],
    filteredAnnotations: [],
    filteredInspections: [],
    filteredScaffolds: [],
    filteredPermits: [],
    filteredContainers: [],
    filtered360Imgs: [],
    filteredCameras: [],
    filteredModels: [],
    filteredPointClouds: [],
    // This value is only used internally to update the components that contains the tags.
    // Because changing the tags [] not reflect on those components well.
    // It would help if you used it as a variable in your function that needs to be updated.
    updateComponentUsingTags: '',

    selectedImage: null,
    selectedLevel: null,
    displayUnit: null,
    areViewsLocked: false,
    _360ThumbnailInfo: { shown: false, imgUrl: '', item: null, x: 0, y: 0 },
    _3dThumbnailInfo: { shown: false, imgUrl: '', item: null, x: 0, y: 0 },
    _2dThumbnailInfo: { shown: false, imgUrl: '', item: null, x: 0, y: 0 },
    isRealityActivityManagerShown: false,
    isRealityDashboardShown: false,
    showComparasionTool: false,
    showCroppingBoxPopup: false,
    showBottomButtons: true,
    showLeftButtons: true,
    isIsolating3D: false,
    isCropBoxVisible: false,
    isCropBoxModeActive360: false,
    isCropBox360Active: false,
    annotations: [],
    inspections: [],
    scaffolds: [],
    projects: [],
    levels: [],
    pointClouds: [],
    // AVA-597: lets the Scans (360) panel tell "still loading" from "failed" from "empty".
    projectsLoadStatus: 'idle',
    levelsLoadStatus: 'idle',
    // AVA-597: deep-frozen scan index, rebuilt by plugins/scanIndexPlugin.js (never a getter: see there).
    scanIndex: EMPTY_SCAN_INDEX,
    cameras: [],
    uploadedModels: [],
    shareFileItems: [],
    //point clouds download
    pointCloudDownloads: [],
    shownPointCloudDownloadInfo: null,
    shareFilePopupInfo: { id: '', shown: false },
    editingPointCloudDownloadInfo: null,

    activityManagerViewIndex: 0,

    lastOpenTabIndex: null,
    lastGlobalSearchSelectedItem: null,
    lastSelectedTagId: '',
    transformMode: 'scale',
    showLoading: false,
    // Held true for the cross-facility 360 zoom (?zoom360) intent so the
    // "Preparing your workspace" overlay stays continuous from click through
    // the fire-and-forget projects load until the panorama is interactive —
    // otherwise the default 2D slot flashes in the gap. See MainView
    // handleTagZoomFromUrl + forgeService loadHandler.
    pending360Zoom: false,
    isCorrespondingPcLoading: false,

    panoramaOffset: '50%',

    activeVideoSrc: null,

    // popups
    successfulMessage: '',
    showAddProperty: false,
    addPropertyCallback: null,
    showCropLevelPopup: false,
    showPointCloudDisplayPopup: false,
    showSelectDashboardPopup: false,

    // 360 viewer
    tooltipObject: null,

    //viewers
    viewer: null,
    planUrn: null,
    planViewer: null,
    mainUrn: null,
    selectedModel: null,
    pdfviewer: null,
    camera3dPosition: null,
    load2dViewerCallback: null,
    load360ViewerCallback: null,
    ignoreModelSelectionEvent: false,
    viewerState: {
      isLoading: false,
      percent: 0,
    },

    fetchedState: null,

    onSelectionChangedCallback: null,

    // ModelBrowser
    isolatedNodes: [],

    //point clouds download
    downloadBoxOffset: 0,
    imagesInCropBox: [],
    isPointCloudDownloadEditing: false,

    activityStatusColor: null,

    // LocalStorage keys
    localStoragePreferencesKeys: {
      doNotShowAgainIsolateLocalStorage: 'DoNotShowAgainIsolate',
    },
    sideOut_minSize: null,
    sideOut_currentSize: null,

    useMoniker: false,
    largeModelScalar: 1.1,
    viewerType: '',

    // Active 3D coloured-isolation legend published by SqlViewDataGrid.
    // Shape: { column: string, entries: [{ value, color, count }] } or null when no coloured isolation is active.
    coloringLegend: null,

    // Facility dictionary for mapping IDs to names (used in harvest status tooltips)
    facilityDict: {},
  },
  getters: {
    getAssetId(state) {
      return store.getters['FACILITY/GET_FACILITY']?.id;
    },
    getCurrentSyncVersion() {
      return store.getters['FACILITY/GET_FACILITY']?.syncVersion ?? 1;
    },
    getSelectedTab(state) {
      return state.selectedTab;
    },
    getIsTabShown(state) {
      return state.isTabShown;
    },
    getAssetName(state) {
      return state.assetName;
    },
    getIsLargeModel(state) {
      return state.isLargeModel;
    },
    getIsIsolating3D(state) {
      return state.isIsolating3D;
    },
    getIsCropBoxVisible(state) {
      return state.isCropBoxVisible;
    },
    isCropBoxModeActive360(state) {
      return state.isCropBoxModeActive360;
    },
    getInspections(state) {
      return state.inspections;
    },
    getShowCroppingBoxPopup(state) {
      return state.showCroppingBoxPopup;
    },
    getShowBottomButtons(state) {
      return state.showBottomButtons;
    },
    getShowLeftButtons(state) {
      return state.showLeftButtons;
    },
    get360ThumbnailPopupInfo(state) {
      return state._360ThumbnailInfo;
    },
    get3dThumbnailPopupInfo(state) {
      return state._3dThumbnailInfo;
    },
    get2dThumbnailPopupInfo(state) {
      return state._2dThumbnailInfo;
    },
    getIsRealityActivityManagerShown(state) {
      return state.isRealityActivityManagerShown;
    },
    getIsRealityDashoardShown(state) {
      return state.isRealityDashboardShown;
    },
    getSuccessMessage(state) {
      return state.successfulMessage;
    },
    getActiveVideoSrc(state) {
      return state.activeVideoSrc;
    },
    getDisplayUnit(state) {
      return state.displayUnit;
    },
    getShowCropLevelPopup(state) {
      return state.showCropLevelPopup;
    },
    getAllTags(state) {
      return state.tags
        .concat(state.annotations)
        .concat(state.inspections)
        .concat(state.scaffolds)
        .concat(store.getters.GET_MANUAL_PIPELINES_TAGS)
        .concat(store.getters.GET_MANUAL_STRUCTURAL_TAGS)
        .concat(store.getters.GET_AMIS_TAGS)
        .concat(store.getters.GET_AUTOMATIC_TAGS)
        .concat(store.getters.GET_STRUCTURAL_TAGS)
        .concat(store.getters.GET_PIPELINES_TAGS)
        .concat(store.getters.GET_ALL_EXISTING_ANOMALIES_TAGS);
    },
    GET_ALL_EXISTING_TAGS(state) {
      return state.tags
        .concat(state.annotations)
        .concat(state.inspections)
        .concat(state.scaffolds)
        .concat(store.getters.GET_MANUAL_PIPELINES_TAGS)
        .concat(store.getters.GET_MANUAL_STRUCTURAL_TAGS)
        .concat(store.getters.GET_ALL_AMIS_TAGS_IDS)
        .concat(store.getters.GET_ALL_EXISTING_AUTOMATIC_TAGS)
        .concat(store.getters.GET_ALL_EXISTING_STRUCTURAL_TAGS)
        .concat(store.getters.GET_ALL_EXISTING_PIPELINES_TAGS)
        .concat(store.getters.GET_ALL_EXISTING_ANOMALIES_TAGS);
    },
    getShowAddProperty(state) {
      return state.showAddProperty;
    },
    getAddPropertyCallback(state) {
      return state.addPropertyCallback;
    },
    getLastOpenTabIndex(state) {
      return state.lastOpenTabIndex;
    },
    getLastGlobalSearchSelectedItem(state) {
      return state.lastGlobalSearchSelectedItem;
    },
    getIsolatedNodes(state) {
      return state.isolatedNodes;
    },
    getShowComparasionTool(state) {
      return state.showComparasionTool;
    },
    getTooltipObject(state) {
      return state.tooltipObject;
    },
    getSelectedModel(state) {
      return state.selectedModel;
    },
    getCamera3dPosition(state) {
      return state.camera3dPosition;
    },
    getLastSelectedTagId(state) {
      return state.lastSelectedTagId;
    },
    getPointCloudDownloads(state) {
      return state.pointCloudDownloads;
    },
    getImagesInCropBox(state) {
      return state.imagesInCropBox;
    },
    getDownloadBoxOffset(state) {
      return state.downloadBoxOffset;
    },
    getPointClouds(state) {
      return state.pointClouds;
    },
    getIsPointCloudDownloadEditing(state) {
      return state.isPointCloudDownloadEditing;
    },
    getIsDrawingAreaToDownloadPC(state) {
      return state.isDrawingAreaToDownloadPC;
    },
    getLevels(state) {
      return state.levels;
    },
    getProjects(state) {
      return state.projects;
    },
    getImages(state) {
      return state.projects.flatMap((o) => o.images);
    },
    getParentSiteName(state) {
      return state.parentSiteName;
    },
    getIsFlutterRunning(state) {
      return !!state.parentSiteName && state.parentSiteName.toLowerCase().includes('flutter');
    },
    getIsAdminUser(state) {
      // return !!state.email && state.email.includes('@modsmanagement.com');
      return state.isAdminUser;
    },
    getTransformMode(state) {
      return state.transformMode;
    },
    getShowLoading(state) {
      return state.showLoading;
    },
    getPending360Zoom(state) {
      return state.pending360Zoom;
    },
    getIsCorrespondingPcLoading(state) {
      return state.isCorrespondingPcLoading;
    },
    getParentModuleUrl(state) {
      return state.parentModuleUrl;
    },
    getIsTreeLoaded(state) {
      return state.isTreeLoaded;
    },
    /**
     * Centralized 3D viewer readiness check.
     * True when: viewer exists AND geometry is loaded AND instance tree is built.
     * Used by viewerNavigationUtils store.watch() and isViewerReady().
     */
    isViewer3DReady(state) {
      return !!state.viewer && state.isGeometryLoaded && state.isTreeLoaded;
    },
    getTestPackageId(state) {
      return state.testPackageId;
    },
    getTestPackageNumber(state) {
      return state.testPackageNumber;
    },
    getIgnoreModelSelectionEvent(state) {
      return state.ignoreModelSelectionEvent;
    },
    getSelectedDbids(state) {
      return state.selectedDbids;
    },
    IS_EMPTY_MODEL(state) {
      return state.isEmptyModel || state.selectedModel === 'Empty Model';
    },
    GET_SHOW_SELECT_DASHBOARD_POPUP(state) {
      return state.showSelectDashboardPopup;
    },
    getLoad360ViewerCallback(state) {
      return state.load360ViewerCallback;
    },
    GET_MAIN_URN(state) {
      return state.mainUrn;
    },
    GET_PLAN_URN(state) {
      return state.planUrn;
    },
    GET_ON_SELECTION_CHANGED_CALLBACK(state) {
      return state.onSelectionChangedCallback;
    },
    GET_SIDOUT_MINSIZE(state) {
      if (state.sideOut_minSize) {
        return state.sideOut_minSize
      } else if (localStorage.getItem('sideOutMinSize')) {
        return Number.parseInt(localStorage.getItem('sideOutMinSize'))
      } else {
        return 600
      }
    },
    GET_SIDOUT_CURRENTSIZE(state) {
      if (state.sideOut_currentSize) {
        return state.sideOut_currentSize
      } else if (localStorage.getItem('sideOutCurrentSize')) {
        return localStorage.getItem('sideOutCurrentSize')
      } else {
        return 600
      }

    },
    GET_VIEWER_TYPE(state) {
      return state.viewerType
    },
    GET_IS_DISPLAY_SETTINGS_VISIBLE(state) {
      return state.isDisplaySettingsVisible;
    },
    GET_IS_MODEL_BROWSER_VISIBLE(state) {
      return state.isModelBrowserVisible;
    },
    GET_SCANS(state) {
      return state.projects?.flatMap((p) => p.images ?? []) ?? []
    },
    /**
     * Get facility dictionary (id → name) for mapping facility IDs to display names.
     * Used by harvest status tooltips in TagCard.vue.
     */
    getFacilityDict(state) {
      return state.facilityDict;
    }
  },
  mutations: {
    RECONCILE_CONFIRMED_TAG_ABSENCE(state, { tagId, facilityId }) {
      removeTagIdFromMarkerArrays(state, tagId, facilityId);
      if (state.AVA_INSPECT) {
        state.AVA_INSPECT.avaInspectTagsCount = state.AVA_INSPECT.avaInspectTags?.length || 0;
      }
      const matchesCurrentPopupMember = item => {
        if (String(item?.id) !== String(tagId)) return false;
        const explicitFacility = item?.facilityId ?? item?.forgeAssetId ?? item?.assetId;
        return explicitFacility == null || String(explicitFacility) === String(facilityId);
      };
      ['_2dThumbnailInfo', '_3dThumbnailInfo', '_360ThumbnailInfo'].forEach(key => {
        const info = state[key];
        if (!info) return;
        if (Array.isArray(info.items)) info.items = info.items.filter(item => !matchesCurrentPopupMember(item));
        if (matchesCurrentPopupMember(info.item)) info.item = info.items?.[0] || null;
        if (!info.item && (!Array.isArray(info.items) || info.items.length === 0)) info.shown = false;
      });
    },
    // Tag lists read `updateComponentUsingTags` as a change signal; any new value re-evaluates them.
    setUpdateComponentUsingTags(state, value) {
      state.updateComponentUsingTags = value;
    },
    INVALIDATE_OBSOLETE_TAG_ANCHOR(state, { tagId, facilityId }) {
      invalidateTagAnchorInMarkerArrays(state, tagId, facilityId);
    },
    UPSERT_EQUIPMENT_TAG(state, tag) {
      upsertTagById(state.tags, tag);
    },
    UPSERT_ANNOTATION_TAG(state, tag) {
      upsertTagById(state.annotations, tag);
    },
    UPSERT_INSPECTION_TAG(state, tag) {
      upsertTagById(state.inspections, tag);
    },
    setSelectedLevel(state, level) {
      state.selectedLevel = level;
    },
    SET_SELECTED_IMAGE(state, image) {
      state.selectedImage = image;
    },
    // PanoramaCompass state mutations. We replace the whole object on each
    // mutation so Vue's "did prop change?" check sees the new reference and
    // any computed reading .isReady / .northOffset / .isInteracting re-runs
    // unconditionally — this is the reactivity bridge that rounds 1-4 were
    // missing when the state lived on the forgeService class instance.
    //
    // Round 6 Pattern #8 fix: SET_PANORAMA_COMPASS_READY no longer silently
    // resets isInteracting (hidden side-effect), and validates northOffset
    // with Number.isFinite so a bad lockedYaw producing NaN doesn't enter
    // a "ready but hidden" state.
    SET_PANORAMA_COMPASS_READY(state, payload) {
      const n = payload && Number.isFinite(payload.northOffset) ? payload.northOffset : null;
      state.panoramaCompassState = {
        isReady: n !== null,
        northOffset: n,
        isInteracting: state.panoramaCompassState.isInteracting,
      };
    },
    RESET_PANORAMA_COMPASS(state) {
      state.panoramaCompassState = {
        isReady: false,
        northOffset: null,
        isInteracting: false,
      };
    },
    SET_PANORAMA_COMPASS_INTERACTING(state, value) {
      state.panoramaCompassState = {
        isReady: state.panoramaCompassState.isReady,
        northOffset: state.panoramaCompassState.northOffset,
        isInteracting: !!value,
      };
    },
    // AVA-437 #7a — see forgeViewerInteracting state declaration above.
    SET_FORGE_VIEWER_INTERACTING(state, v) {
      state.forgeViewerInteracting = !!v;
    },
    setAssetId(state, value) {
      state.assetId = value;
    },
    setIsAdminUser(state, value) {
      state.isAdminUser = value;
    },
    setAssetName(state, value) {
      state.assetName = value;
    },
    setUserId(state, value) {
      state.userId = value;
    },
    setIsLargeModel(state, value) {
      state.isLargeModel = value;
    },
    setParentSiteName(state, value) {
      state.parentSiteName = value;
    },
    setIsPickingTag(state, value) {
      state.isPickingTag = value;
      state.isPickingSubTag = false;
      state.isPickingCropOrigin = false;
      state.isPickingPin = false;
      state.isPickingCmlPoint = false;
      state.isSettingTagPosition = false;
    },
    setIsPickingSubTag(state, value) {
      state.isPickingTag = false;
      state.isPickingSubTag = value;
      state.isPickingCropOrigin = false;
      state.isSettingTagPosition = false;
    },
    setIsPickingCropOrigin(state, value) {
      state.isPickingTag = false;
      state.isPickingSubTag = false;
      state.isPickingCropOrigin = value;
      state.isSettingTagPosition = false;
    },
    setIsPickingPin(state, value) {
      state.isPickingPin = value;
    },
    setIsPickingCmlPoint(state, val) {
      state.isPickingCmlPoint = val;
      if (val) {
        state.isPickingTag = false;
        state.isPickingSubTag = false;
        state.isPickingCropOrigin = false;
        state.isPickingPin = false;
        state.isSettingTagPosition = false;
      }
    },
    setPickingCmlCallback(state, callback) {
      state.pickingCmlCallback = callback;
    },
    setCmlAutoLoadedPcName(state, name) {
      state.cmlAutoLoadedPcName = name;
    },
    // Tag position assignment (follows CML picking pattern)
    setIsSettingTagPosition(state, val) {
      state.isSettingTagPosition = val;
      if (val) {
        state.isPickingTag = false;
        state.isPickingSubTag = false;
        state.isPickingCropOrigin = false;
        state.isPickingPin = false;
        state.isPickingCmlPoint = false;
        state.isCropBoxModeActive360 = false;
      }
    },
    setPositionAssignmentTag(state, tag) {
      state.positionAssignmentTag = tag;
    },
    setPositionAssignmentCallback(state, callback) {
      state.positionAssignmentCallback = callback;
    },
    setPositionAutoLoadedPcName(state, name) {
      state.positionAutoLoadedPcName = name;
    },
    setIsTabShown(state, value) {
      state.isTabShown = value;
    },
    setSelectedTab(state, value) {
      state.selectedTab = value;
    },
    setIsIsolating3D(state, value) {
      state.isIsolating3D = value;
    },
    SET_SPATIAL_FOCUS_SWAP_PENDING(state, value) {
      state.spatialFocusSwapPending = value;
    },
    setIsCropBoxVisible(state, value) {
      state.isCropBoxVisible = value;
    },
    SET_CROP_BOX_MODE_360(state, value) {
      state.isCropBoxModeActive360 = value;
    },
    SET_CROP_BOX_360_ACTIVE(state, value) {
      state.isCropBox360Active = value;
      // When activating, clear all other conflicting pick/crop modes (mirrors setIsPickingTag pattern)
      if (value) {
        state.isPickingTag = false;
        state.isPickingSubTag = false;
        state.isPickingCropOrigin = false;
        state.isPickingPin = false;
        state.isPickingCmlPoint = false;
        state.isCropBoxModeActive360 = false;
        state.isSettingTagPosition = false;
      }
    },
    SET_REFERENCE_POINT_360(state, point) {
      state.referencePoint360 = point;
    },
    SET_SHOW_SCAN_SELECTOR_PANEL_LOADING(state, value) {
      state.showScanSelectorPanelLoading = value;
    },
    SET_HAS_VISIBLE_INDIVIDUAL_SCANS(state, value) {
      state.hasVisibleIndividualScans = !!value;
    },
    // Toggles the nearby-selector crop flow. When deactivating (false),
    // clears the saved view config so the next activation starts clean.
    SET_CROP_FROM_NEARBY_SELECTOR(state, value) {
      state.cropFromNearbySelector = value;
      if (!value) {
        state.savedViewConfig = null;
      }
    },
    SET_NEARBY_CROP_RADIUS(state, radius) {
      state.nearbyCropRadius = radius;
    },
    SET_SAVED_VIEW_CONFIG(state, config) {
      state.savedViewConfig = config;
    },
    SET_SHOW_SCAN_SELECTOR_PANEL(state, { referencePoint }) {
      if (referencePoint) {
        state.scanSelectorReferencePoint = referencePoint;
      }
    },
    setShowCroppingBoxPopup(state, value) {
      state.showCroppingBoxPopup = value;
    },
    setShowBottomButtons(state, value) {
      state.showBottomButtons = value;
    },
    setShowLeftButtons(state, value) {
      state.showLeftButtons = value;
    },
    setSuccessMessage(state, value) {
      state.successfulMessage = value;
    },
    setActiveVideoSrc(state, value) {
      state.activeVideoSrc = value;
    },
    setThumbnailPopupInfo(state, info) {
      if (info.type == '3d') state._3dThumbnailInfo = info.info;
      else if (info.type == '2d') state._2dThumbnailInfo = info.info;
      else if (info.type == '360') state._360ThumbnailInfo = info.info;
    },
    hideThumbnailPopup(state, type) {
      if (type == '3d') state._3dThumbnailInfo.shown = false;
      else if (type == '2d') state._2dThumbnailInfo.shown = false;
      else if (type == '360') state._360ThumbnailInfo.shown = false;
      else {
        state._3dThumbnailInfo.shown = false;
        state._2dThumbnailInfo.shown = false;
        state._360ThumbnailInfo.shown = false;
      }
    },
    setIsRealityActivityManagerShown(state, value) {
      state.isRealityActivityManagerShown = value;
    },
    setIsRealityDashoardShown(state, value) {
      state.isRealityDashboardShown = value;
    },
    setDisplayUnit(state, unit) {
      state.displayUnit = unit;
      state.unitScale = scaleFromDisplayUnit(unit);
    },
    SET_UNIT_SCALE(state, value) {
      state.unitScale = value;
    },
    SET_GLOBAL_OFFSET(state, value) {
      state.globalOffset = value;
    },
    setShowCropLevelPopup(state, value) {
      state.showCropLevelPopup = value;
    },
    SET_POINT_CLOUDS_POPUP_VISIBLE(state, value) {
      state.showPointCloudDisplayPopup = value;
    },
    setTags(state, value) {
      state.tags = value;
    },
    setProjects(state, value) {
      state.projects = value;
    },
    setLevels(state, value) {
      state.levels = value;
    },
    setShow360(state, value) {
      state.userSettings.displaySettings.show360 = value;
    },
    setImagesVisibleInViewers(state, { images, visible }) {
      images.forEach((img) => { img.isVisibleInViewers = visible; });
    },
    setImageLocked(state, { image, locked }) {
      image.isLocked = locked;
    },
    setPointClouds(state, value) {
      state.pointClouds = value;
    },
    setProjectsLoadStatus(state, status) {
      state.projectsLoadStatus = status;
    },
    setLevelsLoadStatus(state, status) {
      state.levelsLoadStatus = status;
    },
    /** AVA-597: only scanIndexPlugin commits this, with a deep-frozen index. */
    setScanIndex(state, index) {
      state.scanIndex = index;
    },
    /**
     * Toggle one point cloud's visibility flag, by name.
     *
     * `isShown` is NOT cosmetic bookkeeping. `MainExtension.getshownPointClouds()`
     * filters on it, and `ForgePotree.updatePointClouds()` is fed only that
     * filtered list — so a cloud whose flag is false loads its root octree and
     * then renders nothing at all, with no error anywhere. Measured live:
     * flipping this flag alone took a loaded cloud from 0 to 131,580 rendered
     * points. Callers of `showPointCloud` must set it FIRST.
     *
     * Committed rather than assigned in place (the rest of the codebase does
     * `o.isShown = x` directly) so the change is trackable in Vue DevTools.
     */
    SET_POINT_CLOUD_SHOWN(state, { name, isShown }) {
      const entry = state.pointClouds.find((pc) => pc.name === name);
      if (entry) entry.isShown = !!isShown;
    },
    setPointCloudDownloads(state, value) {
      state.pointCloudDownloads = value;
    },
    setAnnotations(state, value) {
      state.annotations = value;
    },
    setInspections(state, value) {
      state.inspections = value;
    },
    setScaffolds(state, value) {
      state.scaffolds = value;
    },
    /**
     * Writes the placement of one scaffold-family tag into the canonical collection: patches the
     * entry with the same id, or appends `value` when there is none.
     *
     * `updateScaffolds` only ever replaces, so a tag that was never in the collection - a
     * register-created one, materialized straight into a filtered list - had no way in, and the
     * 3D icons and 360 hotspots that render from here never saw it (AVA-492).
     *
     * An existing entry is patched, never swapped: a narrow `setPosition` response carries the
     * tag, not necessarily every relation the client already holds (linked tags, comments,
     * attachments, documents, children). Replacing the object would drop them client-side, and
     * the next full edit would persist the emptied collections. Only the placement fields the
     * server just changed move across - placement plus the per-facility metadata the server
     * resolves; all of them are declared in the tag constructors (forgeModels.js:103, :109, :140,
     * :143-144, :2219-2221), so plain assignment stays reactive and needs no Vue.set.
     *
     * push rather than an index assignment: Vue 2 cannot observe `arr[i] = x`.
     * Callers own the `updateComponentUsingTags` bump, exactly as they do around the other
     * collection mutations.
     */
    upsertScaffold(state, value) {
      if (!value) return;
      const index = state.scaffolds.findIndex((o) => o.id == value.id);
      if (index === -1) {
        state.scaffolds.push(value);
        return;
      }
      const existing = state.scaffolds[index];
      // Always through the `point` setter, which materializes a Vector3. A cleared position is
      // the ORIGIN, never a literal null: `parseContent` normalises the API's null into a zero
      // vector (forgeModels.js:2373-2381) and both `clone()` and the `point` getter (:2340-2356)
      // assume a Vector3. `isScaffoldPlaced` reads the origin as unplaced, so the renderers stop
      // drawing the tag either way.
      if (value.position) existing.point = value.position;
      else existing.point = { x: 0, y: 0, z: 0 };
      if (value.scale) existing.setScale(value.scale.x, value.scale.y, value.scale.z);
      if (value.max) existing.setMax(value.max.x, value.max.y, value.max.z);
      existing.linked360ImageInfo = value.linked360ImageInfo;
      existing.thumbnailUrl = value.thumbnailUrl;
      // The per-facility placement metadata, copied verbatim so the collection mirrors the
      // server. Arrays are copied rather than shared, as setSameBaseParameters does.
      existing.positionedFacilityIds = value.positionedFacilityIds
        ? [...value.positionedFacilityIds]
        : [];
      existing.otherPositionedFacilityIds = value.otherPositionedFacilityIds
        ? [...value.otherPositionedFacilityIds]
        : [];
      existing.positionImage360Id = value.positionImage360Id;
    },
    setShowAddProperty(state, value) {
      state.showAddProperty = value;
    },
    setAddPropertyCallback(state, value) {
      state.addPropertyCallback = value;
    },
    setLastOpenTabIndex(state, value) {
      state.lastOpenTabIndex = value;
    },
    setLastGlobalSearchSelectedItem(state, value) {
      state.lastGlobalSearchSelectedItem = value;
    },
    setIsolatedNodes(state, value) {
      state.isolatedNodes = value;
    },
    setShowComparasionTool(state, value) {
      state.showComparasionTool = value;
    },
    setTooltipObject(state, value) {
      state.tooltipObject = value;
    },
    setSelectedModel(state, value) {
      state.selectedModel = value;
    },
    setCamera3dPosition(state, value) {
      state.camera3dPosition = value;
    },
    setLastSelectedTagId(state, value) {
      state.lastSelectedTagId = value;
    },
    setImagesInCropBox(state, value) {
      state.imagesInCropBox = value;
    },
    setDownloadBoxOffset(state, value) {
      state.downloadBoxOffset = value;
    },
    setIsPointCloudDownloadEditing(state, value) {
      state.isPointCloudDownloadEditing = value;
    },
    setIsDrawingAreaToDownloadPC(state, value) {
      state.isDrawingAreaToDownloadPC = value;
    },
    setTransformMode(state, value) {
      state.transformMode = value;
    },
    setShowLoading(state, value) {
      state.showLoading = value;
    },
    setPending360Zoom(state, value) {
      state.pending360Zoom = value;
    },
    setIsCorrespondingPcLoading(state, value) {
      state.isCorrespondingPcLoading = value;
    },
    setIsTreeLoaded(state, value) {
      state.isTreeLoaded = value;
    },
    setIsGeometryLoaded(state, value) {
      state.isGeometryLoaded = value;
    },
    setLoad2dViewerCallback(state, value) {
      state.load2dViewerCallback = value;
    },
    setLoad360ViewerCallback(state, value) {
      state.load360ViewerCallback = value;
    },
    setIgnoreModelSelectionEvent(state, value) {
      state.ignoreModelSelectionEvent = value;
    },
    setSelectedDbids(state, value) {
      state.selectedDbids = value;
    },
    SET_IS_EMPTY_MODEL(state, value) {
      state.isEmptyModel = value;
    },
    SET_SHOW_SELECT_DASHBOARD_POPUP(state, value) {
      state.showSelectDashboardPopup = value;
    },
    SET_ON_SELECTION_CHANGED_CALLBACK(state, value) {
      state.onSelectionChangedCallback = value;
    },
    SET_SIDOUT_MINSIZE(state, value) {
      state.sideOut_minSize = value;
      localStorage.setItem('sideOutMinSize', value);
    },
    SET_SIDOUT_CURRENTSIZE(state, value) {
      state.sideOut_currentSize = value
      localStorage.setItem('sideOutCurrentSize', value);
    },
    SET_VIEWER_TYPE(state, value) {
      state.viewerType = value
    },
    SET_COLORING_LEGEND(state, value) {
      state.coloringLegend = value
    },
    SET_IS_DISPLAY_SETTINGS_VISIBLE(state, value) {
      state.isDisplaySettingsVisible = value;
    },
    SET_IS_MODEL_BROWSER_VISIBLE(state, value) {
      state.isModelBrowserVisible = value;
    }
  },
  actions: {
    RECONCILE_TAG_MUTATION_RECEIPTS({ state, commit, getters }, receipts = []) {
      const flattened = receipts.flat(Infinity).filter(Boolean);
      const currentFacilityId = state.assetId;
      const scoped = flattened.filter(receipt => (
        String(receipt.facilityId) === String(currentFacilityId)
        && (receipt.companyId == null
          || String(receipt.companyId) === String(getters?.['ACCOUNT/GET_COMPANY']?.id ?? ''))
        && (receipt.principalId == null
          || String(receipt.principalId) === String(getters?.['ACCOUNT/GET_USER']?.id ?? ''))
      ));
      const confirmed = scoped.filter(receipt => receipt.confirmedAbsentInScope === true);
      confirmed.forEach(receipt => commit('RECONCILE_CONFIRMED_TAG_ABSENCE', receipt));
      const obsolete = scoped.filter(receipt => (
        receipt.outcome === TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED
      ));
      obsolete.forEach(receipt => commit('INVALIDATE_OBSOLETE_TAG_ANCHOR', receipt));

      const manager = state.forgeService?.panoramaHotspotManager;
      scoped.forEach(receipt => manager?.invalidateTagHotspotSelection?.(
        receipt,
        manager.getTagHotspotSelectionGeneration?.(),
        { render: false },
      ));
      const geometry = scoped.filter(receipt => (
        receipt.outcome === TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED
        || (receipt.outcome === TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS
          && (receipt.confirmedAbsentInScope === true
            || Object.prototype.hasOwnProperty.call(receipt, 'authoritativePoint')))
      ));
      if (geometry.length) {
        state.forgeService?.update3dIconsInAllViews?.({ invalidate2DGeometry: true });
        commit('setUpdateComponentUsingTags', Date.now().toString());
      }
      return confirmed.map(receipt => receipt.tagId);
    },
    CLEAR_MAIN_DATA({ dispatch, commit, state }) {
      debugLog('[Store] CLEAR_MAIN_DATA');

      commit('setSelectedTab', -1);
      commit('setIsTabShown', false);

      disposeForgeViewers();

      if (window.ForgePotree) {
        window.ForgePotree.Global.boundingBoxStrict = null;
      }

      state.forgeService = new ForgeViewerService();
      state.userSettings = new UserSettings();
      state.forgeSettings = new ForgeSettings();

      state.model3dAvailable = false;
      state.isEmptyModel = false;

      commit('SET_PAGE_VIEW_3D', null);
      commit('SET_PAGE_VIEW_2D', null);

      state.globalOffset = new THREE.Vector3(0, 0, 0);
      state.unitScale = 0;
      // Clear the unit authority too — a facility without a displayUnit must
      // not inherit the previous facility's unit through the shared store.
      state.displayUnit = null;
      state.pinnedPoint3d = null;
      state.pinnedPoint2d = null;
      state.pinnedPoint360 = null;
      state.referencePoint360 = null;
      state.dataFetched = false;
      state.mainUrn = null;
      state.planUrn = null;
      state.selectedLevel = null;
      state.selectedImage = null;
      state.areViewsLocked = false
      state.showComparasionTool = false

      state.dataFetched = false;

      state.levels = [];
      state.uploadedModels = [];
      state.documentTypes = [];

      state.cameras = [];
      state.pointClouds = [];
      state.pointCloudDownloads = [];
      state.categories = [];
      state.projects = [];
      commit('setProjectsLoadStatus', 'idle');
      commit('setLevelsLoadStatus', 'idle');

      commit('setSelectedModel', null);
      commit('setTags', []);
      commit('setAnnotations', []);
      commit('setScaffolds', []);
      commit('setInspections', []);
      commit('SET_ANOMALIES_TAGS', []);
      commit('SET_ALL_EXISTING_ANOMALIES_TAGS', []);
      commit('SET_ANOMALIES_TAGS_COUNT', 0);
      commit('SET_FILTERED_ANOMALIES_TAGS_IDS', []);
      commit('SET_ALL_EXISTING_AUTOMATIC_TAGS', []);
      commit('SET_AUTOMATIC_TAGS', []);
      commit('SET_MANUAL_PIPELINES_TAGS', []);
      commit('SET_ALL_EXISTING_PIPELINES_TAGS', []);
      commit('SET_MANUAL_STRUCTURAL_TAGS', []);
      commit('SET_ALL_EXISTING_STRUCTURAL_TAGS', []);
      commit('SET_ALL_AMIS_FILTERED_INSPECTIONS', []);
      commit('SET_INSPECTION_TAG_TREE', {});
      commit('SET_AMIS_TAGS', []);
      commit('SET_AMIS_UNITS', []);
      commit('SET_INSPECTION_SELECTED_FILTER', 'No Filter')

      dispatch('RESET_EDITING_TAG');

      commit('SET_IS_VIEW_1_VISIBLE', true);
      commit('SET_IS_VIEW_2_VISIBLE', false);
      commit('SET_IS_VIEW_3_VISIBLE', false);
      commit('SET_IS_VIEW_4_VISIBLE', false);

      commit('SET_VIEW_1_TYPE', '2d');
      commit('SET_VIEW_2_TYPE', null);
      commit('SET_VIEW_3_TYPE', null);
      commit('SET_VIEW_4_TYPE', null);

      dispatch('VIEWERS_MODE/EXIT_VIEWER_MODE');

      dispatch('AI_INSPECT/RESET');
      dispatch('MODEL_VERSIONS/RESET');
      dispatch('INSPECTION_DASHBOARD/RESET');
      dispatch('THICKNESS_DASHBOARD/RESET');
      dispatch('CATHODIC_DASHBOARD/RESET');
      dispatch('IOW_DASHBOARD/RESET');
      dispatch('RBI_DASHBOARD/RESET');
      dispatch('DASHBOARD_FILTERS/RESET');
      dispatch('GLOBAL_DASHBOARD_FILTERS/RESET');
      dispatch('MASTERS/CLEAR_SCHEMAS_CACHE');
    },
    async INITIALIZE_MAIN_DATA({ state, commit, dispatch }) {
      commit('setShowLoading', true);

      let facilityId = router.currentRoute.params.id;

      state.assetId = facilityId;

      fetchUserSettings();
      disposeForgeViewers();

      state.pinnedPoint3d = null;
      state.pinnedPoint2d = null;
      state.pinnedPoint360 = null;
      state.referencePoint360 = null;
      state.dataFetched = false;
      state.mainUrn = null;
      state.planUrn = null;
      state.selectedLevel = null;
      state.selectedImage = null;

      commit('SET_PAGE_VIEW_3D', null);
      commit('SET_PAGE_VIEW_2D', null);

      dispatch('TAGS_GROUPS/GET_TAG_GROUPS');

      dispatch('MODEL_VERSIONS/LOAD');

      // Permission-gated fetches - only call APIs user has permission to access
      // This prevents 403 errors during normal user journey

      if ($has(permissions.Documents.View)) {
        dispatch('FETCH_ALL_DOCUMENTS');
      }

      if ($has(permissions.Tags.Anomaly.View)) {
        dispatch('FETCH_ALL_ANOMALIES_TAGS_INFO', { assetId: facilityId });
      }

      if ($has(permissions.Tags.Equipment.View)) {
        dispatch('fetchAllAutomaticTagsDbids', { assetId: facilityId });
      }

      if ($has(permissions.Tags.Pipeline.View)) {
        dispatch('FETCH_ALL_PIPELINES_TAGS_INFO', { assetId: facilityId });
      }

      if ($has(permissions.Tags.Structural.View)) {
        dispatch('FETCH_ALL_STRUCTURAL_TAGS_INFO', { assetId: facilityId });
      }

      if ($has(permissions.Tags.AvaInspect.View)) {
        dispatch('FETCH_AVA_INSPECT_TAGS', { assetId: facilityId });
      }

      if ($has(permissions.InspectionTags.View)) {
        state.forgeService.loadInspectionTagsAdvanced('', true);
      }

      if ($has(permissions.Areas.View)) {
        dispatch('AREAS/GET_ENTRIES', facilityId);
      }

      if ($has(permissions.Facilities.View)) {
        dispatch('FETCH_FACILITY_DICT');
      }

      // MASTERS/LOAD_SCHEMAS_FOR_FACILITY has NO permission requirement (just auth)
      // Safe to call for all authenticated users
      dispatch('MASTERS/LOAD_SCHEMAS_FOR_FACILITY', facilityId);

      let asset = await fetchAssetModel(facilityId);

      // Load saved global offset and unit scale from database
      try {
        const response = await getModelOffsets(facilityId, FACILITY_REQUEST);

        if (response.data.hasData && response.data.globalOffset) {
          // Populate store with saved values BEFORE viewer loads
          state.globalOffset = new THREE.Vector3(
            response.data.globalOffset.x,
            response.data.globalOffset.y,
            response.data.globalOffset.z
          );
          state.unitScale = response.data.unitScale;

          debugLog('[Forge] Loaded saved model metadata:', {
            modelName: response.data.modelName,
            globalOffset: state.globalOffset,
            unitScale: state.unitScale
          });
        } else {
          debugLog('[Forge] No saved offsets found, will save after model loads');
        }
      } catch (error) {
        debugWarn('[Forge] Failed to load saved offsets:', error);
        // Continue without saved data - will save when model loads
      }

      let p1 = dispatch('GET_MAIN_URN').then((urn) => {
        state.model3dAvailable = !!urn;
      });

      let p2 = fetchAssetModels(facilityId, asset.selectedModel).then((models) => {
        state.uploadedModels = models;
      });

      commit('setLevelsLoadStatus', 'loading');
      let p3 = levelsService.fetchAssetLevelsParsed(facilityId, FACILITY_REQUEST).then((levels) => {
        state.selectedLevel = levels.find((o) => o.name == asset.selectedLevel);
        state.levels = levels;
        commit('setLevelsLoadStatus', 'loaded');
      }).catch((err) => {
        debugWarn('[Store] Facility levels failed to load:', err);
        commit('setLevelsLoadStatus', 'error');
      });

      Promise.allSettled([p1, p2, p3]).then(() => {
        commit('setShowLoading', false);
        state.forgeService.update3dIconsInAllViews();
      });

      handleAssetInfo(asset);

      dispatch('GET_FACILITY_TABS_DATA', facilityId);
    },
    LOAD_FACILITY_PROJECTS({ state, commit }, assetId) {
      const facilityKey = String(assetId || '').toLowerCase();
      const currentFacilityKey = String(state.assetId || '').toLowerCase();
      if (facilityKey && facilityKey === currentFacilityKey && state.projects?.length > 0) {
        return Promise.resolve(state.projects);
      }
      if (facilityProjectsRequests.has(facilityKey)) {
        return facilityProjectsRequests.get(facilityKey);
      }

      const isCurrentFacility = () => String(state.assetId || '').toLowerCase() === facilityKey;
      if (isCurrentFacility()) commit('setProjectsLoadStatus', 'loading');

      const request = projectsService.getByAsset(assetId, FACILITY_REQUEST).then((data) => {
        const projects = ForgeProject.parseProjectsFromDB(data.data);
        const show360 = state.userSettings.displaySettings.show360;
        projects.forEach((project) => project.setAllImagesVisibleInViewers(show360));
        if (isCurrentFacility()) {
          commit('setProjects', projects);
          commit('setProjectsLoadStatus', 'loaded');
          // The viewers may have drawn their icons before the scans arrived; nothing else redraws them.
          state.forgeService?.update360IconsInAllViews();
        }
        return projects;
      }).catch((err) => {
        debugWarn('[Store] Facility image catalog failed to load:', err);
        if (isCurrentFacility()) commit('setProjectsLoadStatus', 'error');
        return [];
      }).finally(() => {
        if (facilityProjectsRequests.get(facilityKey) === request) {
          facilityProjectsRequests.delete(facilityKey);
        }
      });

      facilityProjectsRequests.set(facilityKey, request);
      return request;
    },
    GET_FACILITY_TABS_DATA({ dispatch, commit, state, getters }, assetId) {
      // Permission-gated fetch for document types
      if ($has(permissions.Documents.View)) {
        dispatch('REQUEST_DOCUMENT_TYPES');
      }
      const userId = getters['ACCOUNT/GET_USER']?.id;
      const normalEquipmentRequest = fetchAssetNormalEquipment(assetId).then(
        (normalEquipment) => {
          commit('setTags', normalEquipment);
          commit('SET_AUTOMATIC_TAGS', []);
        },
      );
      const manualPipelinesRequest = fetchAssetManualPipelines(assetId).then(
        (manualPipelines) => {
          commit('SET_MANUAL_PIPELINES_TAGS', manualPipelines);
        },
      );
      const manualStructuralRequest = fetchAssetManualStructural(assetId).then(
        (manualStructural) => {
          commit('SET_MANUAL_STRUCTURAL_TAGS', manualStructural);
        },
      );
      const annotationsRequest = fetchAssetAnnotations(assetId, userId).then((annotations) => {
        commit('setAnnotations', annotations);
      });
      const scaffoldsRequest = fetchAssetScaffold(assetId).then((scaffolds) => {
        commit('setScaffolds', scaffolds);
      });

      // const inspectionsRequest = fetchAssetInspections(assetId).then((inspections) => {
      //   commit('setInspections', inspections);
      //   if (inspections?.length) dispatch('UPDATE_INSPECTIONS_UNITS');
      // });
      Promise.all([
        normalEquipmentRequest,
        manualPipelinesRequest,
        manualStructuralRequest,
        annotationsRequest,
        scaffoldsRequest,
        // inspectionsRequest,
      ]).then((responses) => {
        state.dataFetched = true;
        if (state.forgeService.getMainExtension() && state.viewer && state.viewer.model) {
          state.forgeService.getMainExtension()?.syncTagsToViewer();
        }
        state.forgeService.update3dIconsInAllViews();
        state.forgeService.dbIdPerObj = [];
      });
      fetchAssetBookmarks(assetId).then((cameras) => {
        state.cameras = cameras;
      });
      fetchAssetPointCloudModels(assetId).then((pointClouds) => {
        state.pointClouds = pointClouds;
      });
      fetchAssetPointCloudDownloads(assetId).then((pointCloudDownloads) => {
        state.pointCloudDownloads = pointCloudDownloads;
      });
      fetchAssetCategories(assetId).then((categories) => {
        //to keep the reference to apply on the UI correctly
        state.categories = [...categories];
      });
      dispatch('LOAD_FACILITY_PROJECTS', assetId);
    },
    GET_DATA_FROM_LOCAL_STORAGE({ state, commit }) {
      let encodedKey1 = btoa('inspectionPropertiesWithCheck');
      let v1 = localStorage.getItem(encodedKey1);
      if (v1) {
        commit('SET_INSPECTION_PROPERTIES_WITH_CHECK', JSON.parse(v1));
      }
    },
    SET_VIEWER_THEME() {
      let settingsPanel = store.state.viewer?.getSettingsPanel();

      if (!settingsPanel) return;

      let preferences = settingsPanel.preferences;
      const isDark = store.getters['SETTING/GET_DARK_MODE'];

      if (preferences) {
        // preferences.set('lightPreset', isDark ? 2 : 4);
        // preferences.set('envMapBackground', !isDark);

        // Updated based on [EV-874]
        setTimeout(() => {
          store.state.viewer.setEnvMapBackground(false);
          store.state.viewer.setLightPreset(2);
        }, 0);
      }
    },
    SET_VIEWER_DISPLAY_DEFAULT_PREFERENCES({ state, commit }) {
      let settingsPanel = state.viewer.getSettingsPanel();

      if (!settingsPanel) return;

      const defaultCallbackValue = { detail: { target: { checked: true } } };

      settingsPanel.controls[settingsPanel.antialiasingChkBoxId]?.listeners?.change[0]?.callbackFn(
        defaultCallbackValue,
      );
      settingsPanel.controls[
        settingsPanel.ambientshadowsChkBoxId
      ]?.listeners?.change[0]?.callbackFn(defaultCallbackValue);
      settingsPanel.controls[settingsPanel.groundShadowChkBoxId]?.listeners?.change[0]?.callbackFn(
        defaultCallbackValue,
      );
      settingsPanel.controls[
        settingsPanel.groundReflectionChkBoxId
      ]?.listeners?.change[0]?.callbackFn(defaultCallbackValue);

      settingsPanel.preferences.set('lineRendering', false);
      settingsPanel.preferences.set('displaySectionHatches', false);

      store.dispatch('SET_VIEWER_THEME');
    },
    closeLeftTab({ commit, state, dispatch }, isViewerMinimized = false) {
      state.isTabShown = false;
      if (state.selectedTab == 0) {
        state.forgeService.update3dIconsInAllViews();
      }

      if (!isViewerMinimized) {
        state.selectedTab = null;
        state.lastOpenTabIndex = null;
      }
      let query = { ...router.currentRoute.query };

      if (!isViewerMinimized) {
        if (query.tab || query.tab == 0) {
          delete query.tab;
          router.push({ query: query });
        }
      }

      // Bridge to new panel system - close all panels to stay in sync
      dispatch('GENERAL/CLOSE_ALL_PANELS');
    },
    clearThumbnailPopup(state, type) {
      if (type == '3d')
        state._3dThumbnailInfo = {
          shown: false,
          imgUrl: '',
          item: null,
          x: 0,
          y: 0,
        };
      else if (type == '2d')
        state._2dThumbnailInfo = {
          shown: false,
          imgUrl: '',
          item: null,
          x: 0,
          y: 0,
        };
      else if (type == '360')
        state._360ThumbnailInfo = {
          shown: false,
          imgUrl: '',
          item: null,
          x: 0,
          y: 0,
        };
      else {
        state._3dThumbnailInfo = {
          shown: false,
          imgUrl: '',
          item: null,
          x: 0,
          y: 0,
        };
        state._2dThumbnailInfo = {
          shown: false,
          imgUrl: '',
          item: null,
          x: 0,
          y: 0,
        };
        state._360ThumbnailInfo = {
          shown: false,
          imgUrl: '',
          item: null,
          x: 0,
          y: 0,
        };
      }
    },
    updateImagesInCropbox({ commit, state }) {
      let cropping3DExtension = state.forgeService.getCropping3DExtension();
      let offset = parseFloat(state.downloadBoxOffset) / store.state.unitScale;

      if (!cropping3DExtension.cropBox) {
        commit('setImagesInCropBox', []);
        return;
      }

      let newBox = getBoxOffset(cropping3DExtension.cropBox, offset);
      var imagesInBox = state.forgeService
        .getAllPanoramaImages()
        .filter((o) => newBox.containsPoint(o.getCameraPositionInViewer()));
      commit('setImagesInCropBox', imagesInBox);
      cropping3DExtension.drawOffsetCropBox();
      state.viewer.impl.invalidate(false, false, true);
    },
    updateDisplayUnit({ commit, state }, { displayUnit }) {
      let data = { assetId: state.assetId, displayUnit: displayUnit };
      HttpService.post({
        url: `api/forge/models/updateDisplayUnit`,
        data: data,
      })
        .then(() => {
          // setDisplayUnit also recomputes unitScale — the confirmed unit is
          // the authority, so the session scale must follow it immediately.
          commit('setDisplayUnit', displayUnit);
        })
        .catch((error) => {
          debugLog('[Store] updateDisplayUnit error:', error);
        });
    },
    showLevel({ dispatch, commit, state }, { level }) {
      dispose2DViewer();
      state.levels.forEach((o) => (o.isSelected = false));
      level.isSelected = true;

      if (!level.planUrn) {
        Vue.prototype.$dialog.notify.warning(
          'Plot plan is not yet uploaded, please upload the plot plan first.',
        );
        return;
      }

      state.selectedLevel = level;
      if (!getViewPage2d()) {
        store.state.mainApp?.openViewPageWithType('2d');
      } else {
        store.state.mainApp?.showViewPageWithType('2d');
      }
      setTimeout(() => {
        launchPlanViewer(level.planUrn, null, getStreamingApi(level.apsRegion));
      }, 100);
      checkIsModelTrasnlated(level.planUrn, level.apsRegion, (isTranslated, progress) => {
        if (!isTranslated) {
          if (progress) {
            Vue.prototype.$dialog.notify.warning(
              'Plot plan translating is in-progress, please give it more time to finish translating.',
            );
          } else {
            Vue.prototype.$dialog.notify.warning(
              'Plot plan is not yet uploaded, please upload the plot plan first.',
            );
          }
        }
      });
    },
    removeImageFromProjects({ dispatch, commit, state }, { image }) {
      var targetProject = state.projects.find((o) => o.images.includes(image));
      var index = targetProject.images.findIndex((o) => o == image);
      targetProject.images.splice(index, 1);
    },
    toggleAreaToDownloadPC({ dispatch, commit, state }, { value }) {
      commit('setImagesInCropBox', []);
      commit('setIsDrawingAreaToDownloadPC', value);
      commit('setIsPointCloudDownloadEditing', value);

      if (!value) {
        store.state.forgeService.getDrawPointsExtension()?.toggleMeasuringArea(false);
        store.state.forgeService.getMeasureExtensionInViewer2D()?.deactivate();
      }
    },
    CLEAR_EDIT_TAG_TAB({ dispatch, commit, state }) {
      commit('setIsPickingTag', false);
      commit('SET_EDITING_TAG', null);
      commit('SET_IS_TAG_READ_ONLY', true);
    },

    clearEditAMISTagTab({ dispatch, commit, state }) {
      commit('setIsPickingTag', false);
      commit('SET_EDITING_AMIS_TAG', null);
      commit('SET_IS_AMIS_TAG_READ_ONLY', true);
    },
    // Tags
    addEquipmentTag({ dispatch, commit, state }, { tag }) {
      commit('UPSERT_EQUIPMENT_TAG', tag);
      commit('setUpdateComponentUsingTags', Date.now().toString());
    },
    updateEquipmentTag({ dispatch, commit, state }, { tag }) {
      store.state.forgeService.updateAllLinkedTagsWithTagLinks(tag);
      var parentList = tag.tagHarvesterId ? state.TAGS.automaticTags : state.tags;
      var index = parentList.findIndex((o) => o.id == tag.id);

      if (index == -1) return false;

      parentList.splice(index, 1, tag);

      state.updateComponentUsingTags = !state.updateComponentUsingTags;

      return true;
    },
    updateAnomaly({ dispatch, commit, getters, state }, { tag }) {
      store.state.forgeService.updateAllLinkedTagsWithTagLinks(tag);

      let parentList = getters.GET_ALL_EXISTING_ANOMALIES_TAGS;

      var index = parentList.findIndex((o) => o.id == tag.id);

      if (index == -1) return false;

      parentList.splice(index, 1, tag);

      state.updateComponentUsingTags = !state.updateComponentUsingTags;

      return true;
    },
    // Pipelines
    updatePipeline({ dispatch, commit, getters, state }, { tag }) {
      store.state.forgeService.updateAllLinkedTagsWithTagLinks(tag);

      let parentList = tag.tagHarvesterId
        ? state.TAGS.pipelinesTags
        : getters.GET_MANUAL_PIPELINES_TAGS;

      var index = parentList.findIndex((o) => o.id == tag.id);

      if (index == -1) return false;

      parentList.splice(index, 1, tag);

      state.updateComponentUsingTags = !state.updateComponentUsingTags;

      return true;
    },
    // Structural
    updateStructural({ dispatch, commit, getters, state }, { tag }) {
      store.state.forgeService.updateAllLinkedTagsWithTagLinks(tag);

      let parentList = tag.tagHarvesterId
        ? state.TAGS.structuralTags
        : getters.GET_MANUAL_STRUCTURAL_TAGS;

      var index = parentList.findIndex((o) => o.id == tag.id);

      if (index == -1) return false;

      parentList.splice(index, 1, tag);

      state.updateComponentUsingTags = !state.updateComponentUsingTags;

      return true;
    },
    // Annotations
    updateAnnotation({ dispatch, commit, state }, { annotation }) {
      store.state.forgeService.updateAllLinkedTagsWithTagLinks(annotation);

      var index = state.annotations.findIndex((o) => o.id == annotation.id);

      if (index == -1) return false;

      state.annotations.splice(index, 1, annotation);

      state.updateComponentUsingTags = !state.updateComponentUsingTags;

      return true;
    },
    // Inspections
    updateInspections({ dispatch, commit, state }, { inspection }) {
      store.state.forgeService.updateAllLinkedTagsWithTagLinks(inspection);

      var index = state.inspections.findIndex((o) => o.id == inspection.id);

      if (index == -1) return false;

      state.inspections.splice(index, 1, inspection);

      state.updateComponentUsingTags = !state.updateComponentUsingTags;

      store.state.forgeService.updateAMISTagsColors([inspection]);

      dispatch('UPDATE_AVAILABLE_FILTERS_COLORS', () => {
        let ids = state.TAGS.selectedColorOptions.map((t) => t.id);
        state.TAGS.selectedColorOptions = [
          ...store.state.TAGS.InspectionAvailableColorOptions.filter((i) => ids.includes(i.id)),
        ];
      });

      return true;
    },
    // Scaffolds
    updateScaffolds({ dispatch, commit, state }, { scaffold }) {
      store.state.forgeService.updateAllLinkedTagsWithTagLinks(scaffold);
      var index = state.scaffolds.findIndex((o) => o.id == scaffold.id);

      if (index == -1) return false;

      state.scaffolds.splice(index, 1, scaffold);

      state.updateComponentUsingTags = !state.updateComponentUsingTags;

      return true;
    },
    updateAMISTag({ dispatch, commit, state }, { tag }) {
      // forgeService.updateAllLinkedTagsWithTagLinks(tag)

      var index = state.TAGS.amisTags.findIndex((o) => o.id == tag.id);

      if (index == -1) return false;

      store.state.forgeService.updateAMISTagsColors([tag]);

      state.TAGS.amisTags.splice(index, 1, tag);

      let t = state.TAGS.allAMISFilteredInspections.find((t) => t.id == tag.id);

      if (t == null) {
        state.TAGS.allAMISFilteredInspections.push(tag);
      } else {
        t.point = tag.point;
      }

      state.updateComponentUsingTags = !state.updateComponentUsingTags;

      return true;
    },

    updateTransformMode({ dispatch, commit, state }, { mode }) {
      commit('setTransformMode', mode);

      if (store.state.viewer) {
        store.state.forgeService.updateScaffoldin3D();
      }
    },

    ///
    zoomToPointIn2D(
      { dispatch, commit, state },
      { point3d, levelName, callback, shouldZoom = false },
    ) {
      // Get level containing the point.
      let level =
        state.levels.find((o) => o.name == levelName) ||
        store.state.forgeService.getLevelContainsPoint(point3d);

      // In case no level contains this point but there is a selected level use this level.
      if (
        state.selectedLevel &&
        state.selectedLevel.mappingViewersPointsInfo &&
        state.selectedLevel != level &&
        state.selectedLevel.mappingViewersPointsInfo.isReady() &&
        state.selectedLevel.containsElevation(point3d.z)
      ) {
        level = state.selectedLevel;
      }

      // Show error if the level have no urn.
      if (!level || !level.planUrn) {
        Vue.prototype.$dialog.notify.warning('No Level found for the selected item.');
        return;
      }

      // Show error if the level is not mapped.
      if (
        !state.selectedLevel.mappingViewersPointsInfo ||
        !state.selectedLevel.mappingViewersPointsInfo.isReady()
      ) {
        Vue.prototype.$dialog.notify.warning(
          'Level not mapped, please map level first in admin page.',
        );
        return;
      }

      if (!level) Vue.prototype.$dialog.notify.warning('No Level found for the selected item.');

      if (level != state.selectedLevel) {
        // Set the selected level to the level.
        state.selectedLevel = level ?? state.selectedLevel;

        if (shouldZoom) store.state.forgeService.pointToZoomToIn2d = point3d;

        // Open 2d viewer if not opened yet.
        if (state.planViewer) {
          store.state.mainApp?.openViewPageWithType('2d');
          launchPlanViewer(level.planUrn, null, getStreamingApi(level.apsRegion));
        } else {
          store.state.mainApp?.openViewPageWithType('2d');
        }

        if (callback) commit('setLoad2dViewerCallback', callback);
      } else {
        if (state.planViewer) {
          store.state.mainApp?.openViewPageWithType('2d');
          if (shouldZoom) store.state.forgeService.zoomToPoint2D(point3d);
          if (callback) callback();
        } else {
          if (shouldZoom) store.state.forgeService.pointToZoomToIn2d = point3d;
          store.state.mainApp?.openViewPageWithType('2d');
          if (callback) commit('setLoad2dViewerCallback', callback);
        }
      }
    },
    showSuccessfulMessage({ commit, state }, { message, timeout }) {
      state.successfulMessage = message;

      if (timeout) {
        setTimeout(() => {
          state.successfulMessage = '';
        }, timeout);
      }
    },

    ///
    alert_Forge(_, { message }) {
      getMessagePopup().show({
        title: 'Warning!',
        text: message,
        isAlert: true,
      });
    },
    prompt_Forge(
      _,
      {
        title,
        callback,
        input = {
          inputName: '',
          inputValue: '',
        },
      },
    ) {
      debugLog('[Store] prompt_Forge:', title);
      getMessagePopup().show({
        title: title,
        isConfirm: false,
        isAlert: false,
        callback: callback,
        input: input,
      });
    },
    prompt_Forge_Extended(
      _,
      {
        title,
        callback,
        input = {
          inputName: '',
          inputValue: '',
        },
        extendedInput = {
          inputName: '',
          inputValue: '',
        },
      },
    ) {
      getMessagePopup().showExtendedInput({
        title: title,
        isConfirm: false,
        isAlert: false,
        callback: callback,
        input: input,
        extendedInput: extendedInput,
      });
    },
    confirm_Forge(_, { message, askShowAgain, cancelCallback, callback }) {
      getMessagePopup().show({
        text: message,
        isConfirm: true,
        askShowAgain: !!askShowAgain,
        callback: callback,
        cancelCallback: cancelCallback,
      });
    },
    GET_PROJECTS({ state, commit }) {
      HttpService.get({
        url: `api/forge/projects`,
        params: {
          facilityId: router.currentRoute.params.id,
        },
      }).then((response) => {
        let projects = ForgeProject.parseProjectsFromDB(response.data);
        commit('setProjects', projects);
      }).catch((error) => {
        debugLog('[Store] GET_PROJECTS error:', error);
      });
    },
    /**
     * Fetch minimal facility dictionary (id → name) for current user's accessible facilities.
     * Used for displaying facility names in harvest status tooltips.
     */
    async FETCH_FACILITY_DICT({ state }) {
      try {
        const { data } = await HttpService.get({ url: 'api/Facilities/dictionary', ...FACILITY_REQUEST });
        state.facilityDict = data.reduce((dict, f) => {
          dict[f.id] = f.name;
          return dict;
        }, {});
      } catch (error) {
        debugWarn('[Store] Failed to fetch facility dictionary:', error);
        state.facilityDict = {};
      }
    },
    /**
     * Open a tab with proper panel state management
     * Closes Display Settings and Model Browser before opening the tab
     * @param {number} tabIndex - Index of the tab to open
     */
    OPEN_TAB({ commit, state }, tabIndex) {
      // Close Display Settings if open
      if (state.isDisplaySettingsVisible) {
        commit('SET_IS_DISPLAY_SETTINGS_VISIBLE', false);
      }

      // Close Model Browser if open
      if (state.isModelBrowserVisible) {
        commit('SET_IS_MODEL_BROWSER_VISIBLE', false);
      }

      // Open the requested tab
      commit('setSelectedTab', tabIndex);
      commit('setIsTabShown', true);
    },
    /**
     * Opens a panel tab with validation and state management
     * Replaces mainApp.showPanel() calls throughout the codebase
     * @param {number} tabIndex - Tab index (use PANEL_TAB constants)
     * @param {boolean} pinned - Whether to pin the left menu
     * @returns {boolean} True if panel was opened successfully
     */
    SHOW_PANEL({ commit, state, dispatch }, { tabIndex, pinned = false }) {
      // Validation: Bookmark tab requires 3D viewer
      if (tabIndex === PANEL_TAB.BOOKMARK && !state.viewer) {
        if (window.$dialog) {
          window.$dialog.notify.warning('To open "Saved Views" tab, please open 3D viewer first.', {
            position: 'top-right',
            timeout: 3000,
          });
        }
        return false;
      }

      // Validation: Tab 8 requires model with instance tree
      if (tabIndex === 8 && (!state.viewer || !state.viewer.model || !state.viewer.model.getInstanceTree())) {
        if (window.$dialog) {
          window.$dialog.notify.error('3D Model is not loaded yet.', {
            position: 'top-right',
            timeout: 3000,
          });
        }
        return false;
      }

      // Bridge to new panel system (SideNavigation)
      // Maps legacy tabIndex to panel IDs and opens via GENERAL/OPEN_PANEL
      const panelId = TAB_TO_PANEL_MAP[tabIndex];
      if (panelId) {
        dispatch('GENERAL/OPEN_PANEL', panelId);
      }

      // Save last opened tab
      commit('setLastOpenTabIndex', state.selectedTab);

      // Open the tab
      dispatch('OPEN_TAB', tabIndex);

      // Update 3D icons if needed
      if (tabIndex === PANEL_TAB.SEARCH || state.lastOpenTabIndex === PANEL_TAB.SEARCH) {
        state.forgeService?.update3dIconsInAllViews();
      }

      // Handle pinned state
      const isTouchDevice = 'ontouchstart' in window || !!navigator.maxTouchPoints;
      const hideKeyboard = state.userSettings?.displaySettings?.hideKeyboard;
      if (pinned || (isTouchDevice && !hideKeyboard)) {
        commit('SET_IS_LEFT_MENU_PINNED', true);
      }

      // Resize viewers after panel opens
      setTimeout(() => {
        state.mainApp?.resizeViewers?.();
      }, 100);

      return true;
    },
    /**
     * Open Model Browser with proper panel state management
     * Closes Display Settings before opening Model Browser
     */
    OPEN_MODEL_BROWSER({ commit, state }) {
      // Close Display Settings if open
      if (state.isDisplaySettingsVisible) {
        commit('SET_IS_DISPLAY_SETTINGS_VISIBLE', false);
      }

      // Open Model Browser
      commit('SET_IS_MODEL_BROWSER_VISIBLE', true);
    }
  },
  plugins: [scanIndexPlugin],
  modules: {
    MEASUREMENT,
    TAGS,
    MARKUPS,
    ModelBrowser: ModelBrowser,
    GlobalSearch: GlobalSearch,
    Attachments: Attachments,
    Viewers: Viewers,
    timeline: timeline,
    SharedDashboardSettings: SharedDashboardSettings,
    InspectionDashboard: InspectionDashboard,
    SisTestingDashboard: SisTestingDashboard,
    WorkPackProgressDashboard: WorkPackProgressDashboard,
    ACCOUNT,
    FEEDBACK,
    FEEDBACK_MANAGEMENT,
    VERIFICATION,
    AVA_SNAPS,
    AVA_FINDINGS,
    GENERAL,
    FACILITY,
    COMPANY,
    RELEASE_NOTES,
    USER,
    ROLE,
    PERMISSION,
    USER_INVITATION,
    ADMIN,
    TASK_MANAGER,
    CONTRACT,
    CONTRACTOR,
    PROJECT,
    TASK,
    TASKSTATUS,
    LINK,
    TASK_COMMENTS,
    TASK_HISTORY,
    GANTT,
    SCHEDULER,
    TAGS_TYPES,
    TAGS_REGISTER,
    TAGS_GROUPS,
    GROUP_VIEW,
    ANOMALY_VIEW,
    EQUIPMENT_VIEW,
    PIPELINE_VIEW,
    STRUCTURAL_VIEW,
    OPERATION_VIEW,
    INSPECTION_VIEW,
    AVA_INSPECT,
    TASK_DOCUMENT,
    DOCUMENT_TYPE,
    AI_INSPECT,
    AI_ANALYSIS,
    CORROSION_ANALYSER,
    NOTIFICATIONS,
    DASHBOARD,
    VIEWERS_MODE,
    SHARE_INTENT,
    SCANS_PANEL,
    RBI_DASHBOARD,
    INSPECTION_DASHBOARD,
    THICKNESS_DASHBOARD,
    CATHODIC_DASHBOARD,
    IOW_DASHBOARD,
    DASHBOARD_FILTERS,
    GLOBAL_DASHBOARD_FILTERS,
    AREAS: AREAS,
    MODELS,
    ADMIN_MODELS,
    MODEL_VERSIONS,
    CLIENT,
    GROUPS,
    BLOB_STORAGE,
    DOWNLOAD_MANAGER,
    DOCUMENT_UPLOADS,
    REGISTERS,
    MASTERS,
    TASK_IMPORT,
    DOCUMENT_IMPORTS,
    TOAST,
    NAVIGATION_HISTORY,
    CML_POPUP,
  },
});

// For debugging.
window.vuexStore = store;

// Local
if (config.debugMode && !store.state.parentAPI) {
  store.state.parentAPI = 'https://reality.mods.solutions/api/';
  store.state.parentAPI = 'https://localhost:44359/';
}

export default store;
